// D22 notif-consumer — subscriber-side payload shapes for the
// `notification.*` realtime kinds (Q10 stop-gap until Wave 8 BSPs ship
// Slack/Telegram/email channels).
//
// The producer side (D21) declares the canonical shapes in
// `apps/api/src/do/userHubProto.ts`; this file deliberately re-states the
// same shapes locally so the web consumer branch (`phase3-batch4-22`) can
// compile without a hard import from the API package. The orchestrator
// reconciles any drift in Squash 2 integration.
//
// Keep these shapes byte-identical to D21's emitter payloads.

/** `notification.hotLead` — emitted when the lead-score pipeline flags a
 * lead at or above the "hot" threshold. The toast click-through routes the
 * user to the leads list (lead detail is an overlay, not a route). */
export interface NotificationHotLeadPayload {
  leadId: string;
  conversationId: string;
  /** Display name to show in the toast body. */
  title: string;
  /** 0..100 score the lead landed on. */
  score: number;
  /** Server-side capture timestamp (epoch ms). Used for dedupe ordering. */
  capturedAt: number;
}

/** `notification.reviewPending` — emitted when an AI draft is queued for
 * human review (Review mode + sensitive content). Click-through routes the
 * user to the inbox at the originating conversation. */
export interface NotificationReviewPendingPayload {
  draftId: string;
  conversationId: string;
  /** Contact display name for the toast body. */
  contactName: string;
  /** When the draft entered the review queue (epoch ms). */
  queuedAt: number;
}

/** Realtime frame envelope as it arrives at the subscriber callback. The
 * `UserHubClient` normalises `kind` → `type` before dispatch, so we read
 * `type` here. `payload` is unknown at the protocol level; each kind's
 * handler narrows it down with its own type predicate. */
export interface NotificationFrame<TPayload> {
  type?: string;
  payload?: TPayload | unknown;
}

export const NOTIFICATION_HOT_LEAD_KIND = 'notification.hotLead' as const;
export const NOTIFICATION_REVIEW_PENDING_KIND = 'notification.reviewPending' as const;

/** Narrow guard for hotLead payloads — defends against malformed server
 * frames so a bad emit can't crash the toast bus. */
export function isHotLeadPayload(value: unknown): value is NotificationHotLeadPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.leadId === 'string' &&
    typeof v.conversationId === 'string' &&
    typeof v.title === 'string' &&
    typeof v.score === 'number' &&
    typeof v.capturedAt === 'number'
  );
}

/** Narrow guard for reviewPending payloads. */
export function isReviewPendingPayload(value: unknown): value is NotificationReviewPendingPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.draftId === 'string' &&
    typeof v.conversationId === 'string' &&
    typeof v.contactName === 'string' &&
    typeof v.queuedAt === 'number'
  );
}
